/**
 * The desk's live socket (`/ws`): one per tab while a rep is signed in. The first message is the
 * access token (never in the URL); after that the server pushes call cards, the after-call prompt,
 * whether calls are delayed, and the team's Dialpad status. Reconnects with backoff (1 s doubling to 30 s, plus jitter); a
 * stale token gets one refresh and a reconnect, a second refusal signs the rep out like `rpc.ts`.
 */
import { liveServerMessage, type TeamMember } from '@lsc/contract';
import { type CallCard } from '@lsc/domain';
import { useSyncExternalStore } from 'react';
import { accessToken, refreshAccessToken, signOut as authSignOut } from './auth';

export type LiveStatus = 'connecting' | 'live' | 'reconnecting' | 'offline';

/** A refresh either yields a token, or says whether the session is gone or Supabase just failed. */
export type RefreshResult = { token: string } | { error: 'rejected' | 'transient' };

export interface LiveState {
  status: LiveStatus;
  calls: 'ok' | 'delayed';
  lastEventAt: number | null;
  cards: CallCard[];
  afterCall: { card: CallCard; expiresAt: number } | null;
  /** Who Dialpad has a status for; null until the first `team` message. */
  team: TeamMember[] | null;
  /** This desk's own profile, from `hello` (a call someone else answered clears from the strip). */
  profileId: string | null;
  /** Who has the ticket this tab shows open right now (you included), from `viewers`. */
  viewers: { ticketId: string; people: { profileId: string; name: string }[] } | null;
}

export interface LiveDeps {
  url?: string;
  WebSocketImpl?: typeof WebSocket;
  random?: () => number;
  token?: () => Promise<string | null>;
  refresh?: () => Promise<RefreshResult>;
  signOut?: (reason: string) => Promise<void>;
  online?: () => boolean;
  /** A ticket changed somewhere; the root wires this to the query cache so pages refetch on their own. */
  onTicket?: (notice: TicketNotice) => void;
}

export interface TicketNotice {
  ticketId: string;
  ticketNumber: string | null;
  kind: string;
  eventId: number | null;
  /** Profile ids who were @mentioned: if yours is here, your notifications changed. */
  mentioned: string[];
}

const INITIAL: LiveState = { status: 'connecting', calls: 'ok', lastEventAt: null, cards: [], afterCall: null, team: null, profileId: null, viewers: null };
const OFFLINE_AFTER = 5;
/** Ended cards the desk keeps (the hub keeps the same window); older ones leave the store. */
const ENDED_KEEP_MS = 10 * 60_000;
const fresh = (cards: CallCard[], at: number) => cards.filter((c) => c.state !== 'ended' || (c.endedAt ?? at) > at - ENDED_KEEP_MS);
const SESSION_ENDED = 'Your session ended. Sign in again.';
const NOT_STAFF = 'This desk is for Lifelong staff';

let state: LiveState = INITIAL;
const listeners = new Set<() => void>();
const set = (patch: Partial<LiveState>) => {
  state = { ...state, ...patch };
  for (const fn of listeners) fn();
};

export function getLive(): LiveState {
  return state;
}

/** This desk's own profile id from `hello`, for code outside React. */
export function liveProfileId(): string | null {
  return state.profileId;
}

export function useLive(): LiveState {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => state,
    () => state,
  );
}

export function dismissAfterCall(): void {
  set({ afterCall: null });
}

export function backoffMs(attempt: number, random: () => number): number {
  return Math.min(30_000, 1000 * 2 ** attempt) + Math.floor(random() * 250);
}

function defaultUrl(): string {
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
}

let stopCurrent: (() => void) | null = null;
/** The ticket this tab shows; sent after every hello, so a reconnect tells the hub again. */
let viewingTicket: string | null = null;
let sendViewing: ((ticketId: string | null) => void) | null = null;

/** The ticket page calls this on open and with null on leave. */
export function setViewing(ticketId: string | null): void {
  if (viewingTicket === ticketId) return;
  viewingTicket = ticketId;
  if (state.viewers && state.viewers.ticketId !== ticketId) set({ viewers: null });
  sendViewing?.(ticketId);
}

/** Opens the socket and keeps it open until the returned stop is called. */
export function startLive(deps: LiveDeps = {}): () => void {
  stopCurrent?.();
  const url = deps.url ?? defaultUrl();
  const WS = deps.WebSocketImpl ?? WebSocket;
  const random = deps.random ?? Math.random;
  const token = deps.token ?? accessToken;
  const refresh = deps.refresh ?? refreshAccessToken;
  const signOut = deps.signOut ?? authSignOut;
  const online = deps.online ?? (() => navigator.onLine);

  let stopped = false;
  let ws: WebSocket | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let failures = 0;
  let refreshed = false;
  set({ ...INITIAL });

  const end = (reason: string) => {
    stopped = true;
    void signOut(reason);
  };

  const retryLater = () => {
    failures += 1;
    set({ status: failures >= OFFLINE_AFTER || !online() ? 'offline' : 'reconnecting' });
    timer = setTimeout(() => {
      timer = null;
      connect();
    }, backoffMs(failures - 1, random));
  };

  /** The token was refused or missing: refresh once. Only a refused refresh signs the rep out. */
  const reauth = () => {
    if (refreshed) {
      end(SESSION_ENDED);
      return;
    }
    refreshed = true;
    set({ status: 'reconnecting' });
    void refresh()
      .catch((): RefreshResult => ({ error: 'transient' }))
      .then((r) => {
        if (stopped) return;
        if ('token' in r) connect(r.token);
        else if (r.error === 'rejected') end(SESSION_ENDED);
        else {
          // Supabase or the network hiccuped: try again later, and allow another refresh then.
          refreshed = false;
          retryLater();
        }
      });
  };

  const connect = (tokenOverride?: string) => {
    if (stopped) return;
    const sock = new WS(url);
    ws = sock;
    sock.onopen = () => {
      void (tokenOverride !== undefined ? Promise.resolve(tokenOverride) : token()).then((t) => {
        if (stopped || ws !== sock) return;
        if (!t) {
          ws = null;
          sock.close();
          reauth();
          return;
        }
        sock.send(JSON.stringify({ type: 'auth', token: t }));
      });
    };
    sock.onmessage = (e: MessageEvent) => {
      let msg;
      try {
        msg = liveServerMessage.parse(JSON.parse(String(e.data)));
      } catch {
        return;
      }
      const at = Date.now();
      switch (msg.type) {
        case 'hello':
          failures = 0;
          refreshed = false;
          set({ status: 'live', calls: msg.calls, cards: fresh(msg.cards, at), lastEventAt: at, profileId: msg.profileId });
          sendViewing = (ticketId) => {
            if (ws === sock && sock.readyState === 1) sock.send(JSON.stringify({ type: 'viewing', ticketId }));
          };
          if (viewingTicket) sendViewing(viewingTicket);
          break;
        case 'viewers':
          if (msg.ticketId === viewingTicket) set({ viewers: { ticketId: msg.ticketId, people: msg.people } });
          break;
        case 'card': {
          const i = state.cards.findIndex((c) => c.rootCallId === msg.card.rootCallId);
          const cards = i === -1 ? [...state.cards, msg.card] : state.cards.map((c, j) => (j === i ? msg.card : c));
          const afterCall = state.afterCall?.card.rootCallId === msg.card.rootCallId ? { ...state.afterCall, card: msg.card } : state.afterCall;
          set({ cards: fresh(cards, at), afterCall, lastEventAt: at });
          break;
        }
        case 'after_call':
          set({ afterCall: { card: msg.card, expiresAt: msg.expiresAt }, lastEventAt: at });
          break;
        case 'status':
          set({ calls: msg.calls, lastEventAt: at });
          break;
        case 'team':
          set({ team: msg.people });
          break;
        case 'ticket':
          set({ lastEventAt: at });
          deps.onTicket?.({ ticketId: msg.ticketId, ticketNumber: msg.ticketNumber, kind: msg.kind, eventId: msg.eventId, mentioned: msg.mentioned });
          break;
        case 'presence': {
          const team = state.team ?? [];
          const i = team.findIndex((m) => m.email === msg.member.email);
          set({ team: i === -1 ? [...team, msg.member] : team.map((m, j) => (j === i ? msg.member : m)) });
          break;
        }
      }
    };
    sock.onclose = (e: CloseEvent) => {
      if (stopped || ws !== sock) return;
      ws = null;
      sendViewing = null;
      if (e.code === 4403) {
        end(NOT_STAFF);
        return;
      }
      if (e.code === 4401) {
        reauth();
        return;
      }
      retryLater();
    };
  };

  connect();
  const stop = () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    ws?.close();
    ws = null;
    if (stopCurrent === stop) stopCurrent = null;
  };
  stopCurrent = stop;
  return stop;
}
